import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../css/ResetPassword.css'; // We'll create this CSS file next

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();
  
  const token = new URLSearchParams(window.location.search).get('token');

  const checkPasswordStrength = (value) => {
    let strength = 0;
    if (value.length >= 8) strength++;
    if (value.match(/[A-Z]/)) strength++;
    if (value.match(/[0-9]/)) strength++;
    if (value.match(/[^A-Za-z0-9]/)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    checkPasswordStrength(value);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMsg('');

    if (password !== confirmPassword) {
      setMsg("Passwords don't match");
      setIsLoading(false);
      return;
    }

    if (passwordStrength < 3) {
      setMsg("Password is too weak");
      setIsLoading(false);
      return;
    }

    try {
      const res = await axios.post('/api/reset-password.php', { 
        token, 
        password 
      });
      
      if (res.data.success) {
        setMsg("Password reset successfully! Redirecting...");
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMsg(res.data.message || "Failed to reset password");
      }
    } catch (err) {
      setMsg(err.response?.data?.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-container">
      <div className="reset-card">
        <div className="reset-header">
          <h2>Reset Your Password</h2>
          <p>Create a new password for your account</p>
        </div>

        <form onSubmit={resetPassword} className="reset-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={handlePasswordChange}
              required
              minLength="8"
            />
            <div className="password-strength">
              <div 
                className={`strength-bar ${passwordStrength > 0 ? 'active' : ''}`}
                style={{ backgroundColor: passwordStrength > 0 ? getStrengthColor(passwordStrength) : '' }}
              ></div>
              <div 
                className={`strength-bar ${passwordStrength > 1 ? 'active' : ''}`}
                style={{ backgroundColor: passwordStrength > 1 ? getStrengthColor(passwordStrength) : '' }}
              ></div>
              <div 
                className={`strength-bar ${passwordStrength > 2 ? 'active' : ''}`}
                style={{ backgroundColor: passwordStrength > 2 ? getStrengthColor(passwordStrength) : '' }}
              ></div>
              <div 
                className={`strength-bar ${passwordStrength > 3 ? 'active' : ''}`}
                style={{ backgroundColor: passwordStrength > 3 ? getStrengthColor(passwordStrength) : '' }}
              ></div>
            </div>
            <div className="password-hints">
              {passwordStrength < 3 && (
                <ul>
                  <li className={password.length >= 8 ? 'valid' : ''}>At least 8 characters</li>
                  <li className={password.match(/[A-Z]/) ? 'valid' : ''}>Contains uppercase letter</li>
                  <li className={password.match(/[0-9]/) ? 'valid' : ''}>Contains number</li>
                  <li className={password.match(/[^A-Za-z0-9]/) ? 'valid' : ''}>Contains special character</li>
                </ul>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button 
            type="submit" 
            className="reset-button"
            disabled={isLoading || passwordStrength < 3}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          {msg && (
            <div className={`message ${msg.includes('successfully') ? 'success' : 'error'}`}>
              {msg}
            </div>
          )}
        </form>

      
      </div>
    </div>
  );
}

function getStrengthColor(strength) {
  switch(strength) {
    case 1: return '#ef233c'; // Weak (red)
    case 2: return '#ff9f1c'; // Moderate (orange)
    case 3: return '#2ec4b6'; // Good (teal)
    case 4: return '#4361ee'; // Strong (blue)
    default: return '#e9ecef'; // Default (gray)
  }
}